import type {
  CustomColumn,
  ExprNode,
  CustomColumnFormat,
} from "./customColumns";

// Each pin matcher is a partial key-value map of dimension column values.
// A row matches if ALL specified keys match. Partial matchers (e.g. just
// { TeamOffense: "Portland Trail Blazers" }) match every row with that team.
export type PinMatcher = Record<string, string>;

// Positional-array serialization of an ExprNode for compact URL encoding.
// Object form is used in TS state; this form lives only inside encoded snapshots.
//   { t: "col", f: "Poss" }                   → ["c", "Poss"]
//   { t: "num", v: 100 }                       → 100  (numeric literal short-circuit)
//   { t: "neg", e: ... }                       → ["n", <serialized>]
//   { t: "op", o: "/", l: ..., r: ... }        → ["/", <l>, <r>]
//   { t: "fn", n: "min", a: [...] }            → ["f", "min", <a0>, <a1>, ...]
export type SerializedExpr = number | (string | SerializedExpr)[];

export interface SerializedCustomColumn {
  f: string; // field
  h?: string; // headerName
  e: SerializedExpr; // expression
  d?: 0 | 1 | 2; // format.decimals (omit when auto)
  p?: 1; // format.percent (omit when false)
  sg?: 1; // format.signed (omit when false)
}

export interface TableStateSnapshot {
  s?: Array<{ field: string; sort: "asc" | "desc" }>;
  v?: Record<string, boolean>;
  pc?: { left?: string[]; right?: string[] };
  pr?: { top?: PinMatcher[]; bottom?: PinMatcher[] };
  q?: string;
  r?: "ranks" | "percentiles" | "off";
  f?: Array<{ field: string; operator: string; value?: any }>;
  ver?: 1;
  cc?: SerializedCustomColumn[];
  o?: string[];
  cw?: Record<string, number>;
  rn?: Record<string, string>;
  /** Rank direction overrides by field, in the offense's terms (a defense view flips them): h(igher) / l(ower) /
   *  n(eutral) is better (ExplorerDataGrid/columnRanks.ts) */
  rd?: Record<string, "h" | "l" | "n">;
  /** Perspective pick: "off" / "def" (absent = the detected one) */
  pv?: "off" | "def";
  /** Rank across these breakdown columns (absent = all rows); one column is written as a string */
  ra?: string | string[];
  /** Display labels for breakdown values: field -> stored value -> label (ValueLabelsDialog) */
  vl?: Record<string, Record<string, string>>;
}

export function serializeExpr(node: ExprNode): SerializedExpr {
  switch (node.t) {
    case "col":
      return ["c", node.f];
    case "num":
      return node.v;
    case "neg":
      return ["n", serializeExpr(node.e)];
    case "op":
      return [node.o, serializeExpr(node.l), serializeExpr(node.r)];
    case "fn":
      return ["f", node.n, ...node.a.map(serializeExpr)];
  }
}

export function deserializeExpr(s: SerializedExpr): ExprNode | null {
  if (typeof s === "number") {
    return Number.isFinite(s) ? { t: "num", v: s } : null;
  }
  if (!Array.isArray(s) || s.length === 0) return null;
  const tag = s[0];
  if (tag === "c") {
    return typeof s[1] === "string" ? { t: "col", f: s[1] } : null;
  }
  if (tag === "n") {
    const inner = deserializeExpr(s[1] as SerializedExpr);
    return inner ? { t: "neg", e: inner } : null;
  }
  if (tag === "f") {
    if (typeof s[1] !== "string") return null;
    const args: ExprNode[] = [];
    for (let i = 2; i < s.length; i++) {
      const arg = deserializeExpr(s[i] as SerializedExpr);
      if (!arg) return null;
      args.push(arg);
    }
    return { t: "fn", n: s[1], a: args };
  }
  if (tag === "+" || tag === "-" || tag === "*" || tag === "/") {
    const l = deserializeExpr(s[1] as SerializedExpr);
    const r = deserializeExpr(s[2] as SerializedExpr);
    if (!l || !r) return null;
    return { t: "op", o: tag, l, r };
  }
  return null;
}

export function serializeCustomColumn(
  cc: CustomColumn,
): SerializedCustomColumn {
  const out: SerializedCustomColumn = {
    f: cc.field,
    e: serializeExpr(cc.expr),
  };
  if (cc.headerName) out.h = cc.headerName;
  if (cc.format?.decimals !== undefined) out.d = cc.format.decimals;
  if (cc.format?.percent) out.p = 1;
  if (cc.format?.signed) out.sg = 1;
  return out;
}

export function deserializeCustomColumn(
  s: SerializedCustomColumn,
): CustomColumn | null {
  if (!s || typeof s.f !== "string" || !s.e) return null;
  const expr = deserializeExpr(s.e);
  if (!expr) return null;
  const format: CustomColumnFormat = {};
  if (s.d !== undefined) format.decimals = s.d;
  if (s.p) format.percent = true;
  if (s.sg) format.signed = true;
  const cc: CustomColumn = { field: s.f, expr };
  if (s.h) cc.headerName = s.h;
  if (Object.keys(format).length > 0) cc.format = format;
  return cc;
}

export function encodeTableState(snapshot: TableStateSnapshot): string {
  const json = JSON.stringify(snapshot);
  // btoa with URL-safe alphabet: + → -, / → _, strip = padding
  return btoa(json).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function decodeTableState(param: string): TableStateSnapshot | null {
  try {
    // Restore standard base64: - → +, _ → /, re-pad
    let b64 = param.replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4;
    if (pad) b64 += "=".repeat(4 - pad);
    const json = atob(b64);
    const parsed = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null) return null;
    return parsed as TableStateSnapshot;
  } catch {
    return null;
  }
}

export function buildUrlWithTableState(
  snapshot: TableStateSnapshot | null,
): string {
  const url = new URL(window.location.href);
  if (snapshot && !isEmptySnapshot(snapshot)) {
    url.searchParams.set("ts", encodeTableState(snapshot));
  } else {
    url.searchParams.delete("ts");
  }
  return url.toString();
}

function isEmptySnapshot(snapshot: TableStateSnapshot): boolean {
  return (
    (!snapshot.s || snapshot.s.length === 0) &&
    (!snapshot.v || Object.keys(snapshot.v).length === 0) &&
    !snapshot.pc &&
    !snapshot.pr &&
    !snapshot.q &&
    !snapshot.r &&
    (!snapshot.f || snapshot.f.length === 0) &&
    (!snapshot.cc || snapshot.cc.length === 0) &&
    (!snapshot.o || snapshot.o.length === 0) &&
    (!snapshot.cw || Object.keys(snapshot.cw).length === 0) &&
    (!snapshot.rn || Object.keys(snapshot.rn).length === 0) &&
    (!snapshot.rd || Object.keys(snapshot.rd).length === 0) &&
    !snapshot.pv &&
    !snapshot.ra &&
    (!snapshot.vl || Object.keys(snapshot.vl).length === 0)
  );
}

export function isDefaultState(
  snapshot: TableStateSnapshot,
  isMobile: boolean,
): boolean {
  if (isEmptySnapshot(snapshot)) return true;

  // Check sort: default is [{ field: "Poss", sort: "desc" }]
  const hasDefaultSort =
    !snapshot.s ||
    snapshot.s.length === 0 ||
    (snapshot.s.length === 1 &&
      snapshot.s[0].field === "Poss" &&
      snapshot.s[0].sort === "desc");

  // Check rank mode: default is "percentiles"
  const hasDefaultRank = !snapshot.r || snapshot.r === "percentiles";

  // Check column visibility: default is {} on desktop, { Rank: false } on mobile
  const hasDefaultVisibility = (() => {
    if (!snapshot.v || Object.keys(snapshot.v).length === 0) {
      return !isMobile; // Empty is default on desktop only
    }
    if (isMobile) {
      const keys = Object.keys(snapshot.v);
      return keys.length === 1 && snapshot.v["Rank"] === false;
    }
    return false;
  })();

  // Check pinned columns: default is { left: ["Rank"], right: ["actions"] } desktop, { left: ["Rank"] } mobile
  const hasDefaultPinnedCols = (() => {
    if (!snapshot.pc) return true;
    const left = snapshot.pc.left ?? [];
    const right = snapshot.pc.right ?? [];
    if (isMobile) {
      return left.length === 1 && left[0] === "Rank" && right.length === 0;
    }
    return (
      left.length === 1 &&
      left[0] === "Rank" &&
      right.length === 1 &&
      right[0] === "actions"
    );
  })();

  // No filters, no pinned rows, no quick filter, no custom cols / order / widths / renames
  const hasNoFilters = !snapshot.f || snapshot.f.length === 0;
  const hasNoPinnedRows =
    !snapshot.pr ||
    ((snapshot.pr.top ?? []).length === 0 &&
      (snapshot.pr.bottom ?? []).length === 0);
  // Note: pr now contains PinMatcher[] (key-value objects), not number[]
  const hasNoQuickFilter = !snapshot.q;
  const hasNoCustomCols = !snapshot.cc || snapshot.cc.length === 0;
  const hasNoOrder = !snapshot.o || snapshot.o.length === 0;
  const hasNoWidths = !snapshot.cw || Object.keys(snapshot.cw).length === 0;
  const hasNoRenames = !snapshot.rn || Object.keys(snapshot.rn).length === 0;
  const hasNoDirections =
    (!snapshot.rd || Object.keys(snapshot.rd).length === 0) &&
    !snapshot.pv &&
    !snapshot.ra &&
    (!snapshot.vl || Object.keys(snapshot.vl).length === 0);

  return (
    hasDefaultSort &&
    hasDefaultRank &&
    hasDefaultVisibility &&
    hasDefaultPinnedCols &&
    hasNoFilters &&
    hasNoPinnedRows &&
    hasNoQuickFilter &&
    hasNoCustomCols &&
    hasNoOrder &&
    hasNoWidths &&
    hasNoRenames &&
    hasNoDirections
  );
}

/** Check if a row's values satisfy all key-value pairs in a matcher. */
export function rowMatchesPinMatcher(
  matcher: PinMatcher,
  row: Record<string, any>,
): boolean {
  return Object.entries(matcher).every(
    ([key, val]) => String(row[key] ?? "") === val,
  );
}

/**
 * Resolve an array of pin matchers against data rows.
 * Returns matching row indices, preserving matcher order then data order.
 * Each row can only appear once (first matcher wins).
 */
export function resolvePinMatchers(
  matchers: PinMatcher[],
  data: Record<string, any>[],
): number[] {
  const ids: number[] = [];
  const matched = new Set<number>();
  for (const matcher of matchers) {
    for (let i = 0; i < data.length; i++) {
      if (matched.has(i)) continue;
      if (rowMatchesPinMatcher(matcher, data[i])) {
        ids.push(i);
        matched.add(i);
      }
    }
  }
  return ids;
}

/**
 * Build a pin matcher from a row using only the specified dimension columns.
 */
export function buildPinMatcher(
  row: Record<string, any>,
  dimensionColumns: string[],
): PinMatcher {
  const matcher: PinMatcher = {};
  for (const col of dimensionColumns) {
    if (row[col] != null) matcher[col] = String(row[col]);
  }
  return matcher;
}
